import {
  Box,
  Button,
  CheckboxField,
  Flex,
  JaneGoldShadowLogo,
  JaneLogo,
  JanePayLogoWhite,
  PartnerLogo,
  Typography,
} from '@iheartjane/reefer';
import { clsx } from 'clsx';
import type { CSSProperties } from 'react';
import { useState } from 'react';

import { MarketingConsent } from '../../../marketingConsent/MarketingConsent';
import sparklyBackground from '../assets/sparkly-background.svg';
import styles from './successScreen.module.css';

export interface SuccessScreenProps {
  /** Whether to hide the Jane Pay branding */
  hideJanePayBranding: boolean;

  /** Whether the user is Jane Pay */
  isJanePay: boolean;

  /** Whether the component is being used in marketplace */
  isMarketplace: boolean;

  /** Location where the component is being used */
  location?: string;

  /** Function called when 'done' button is clicked */
  onDoneClick(optOut?: boolean): void;

  /** Store photo */
  storePhoto?: string;
}

export const SuccessScreen = ({
  hideJanePayBranding,
  isJanePay,
  isMarketplace,
  location,
  onDoneClick,
  storePhoto,
}: SuccessScreenProps) => {
  const [optOut, setOptOut] = useState(
    !isJanePay && !isMarketplace ? false : undefined
  );
  const noImage = isJanePay && hideJanePayBranding && !storePhoto;
  const sparkly = !isJanePay;

  return (
    <Flex
      width="100%"
      height="100%"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
    >
      <Box
        background="gold-light"
        height="100%"
        className={styles['boxStyled']}
      >
        <div
          className={clsx(styles['background'], {
            [styles['background_sparkly']]: sparkly,
            [styles['background_branded']]: !sparkly && !hideJanePayBranding,
          })}
          style={
            sparkly
              ? ({
                  '--successScreen-sparkly-bg': `url("${sparklyBackground}")`,
                } as CSSProperties)
              : undefined
          }
        >
          <Flex
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            width="100%"
            height="100%"
            py={noImage ? 0 : 24}
          >
            {isJanePay && !hideJanePayBranding && (
              <JanePayLogoWhite my={40} height="41px" />
            )}

            {isJanePay && hideJanePayBranding && storePhoto && (
              <PartnerLogo
                name="store-logo"
                variant="store"
                image={storePhoto}
              />
            )}

            {!isJanePay && <JaneLogo size="xs" />}

            {!isJanePay && <JaneGoldShadowLogo height="55px" />}
          </Flex>
        </div>
      </Box>
      <Typography
        variant="header-bold"
        textAlign="center"
        mt={24}
        mb={8}
        branded
      >
        {hideJanePayBranding
          ? 'Success!'
          : `Welcome to ${isJanePay ? 'Jane Pay' : 'Jane Gold!'}`}
      </Typography>
      {!isJanePay && (
        <>
          <Typography textAlign="center" mb={24}>
            You can now earn cash back rewards from your favorite brands
            directly to your linked bank account.
          </Typography>
          {!isMarketplace && (
            <Flex mb={24} width="100%" flexDirection="row">
              <CheckboxField
                disableMobileInputStyling
                name="promotionsOptIn"
                label="I consent to Jane using my email and in-app notifications to send me marketing materials, special offers, recommendations and more, in accordance with Jane's Privacy Policy."
                labelHidden
                defaultChecked
                onChange={(value) => setOptOut(!value)}
              />
              <MarketingConsent />
            </Flex>
          )}
        </>
      )}

      {isJanePay && (
        <Typography textAlign="center" mb={24}>
          {!hideJanePayBranding
            ? 'You successfully linked your bank account. You can now use Jane Pay to checkout on Jane and other participating cannabis stores.'
            : 'You successfully linked your bank account to check out on Jane and other participating cannabis stores, powered by Jane and Aeropay.'}
        </Typography>
      )}

      <Button
        full
        className={clsx({
          [styles['themedButton_janePay']]: isJanePay && !hideJanePayBranding,
        })}
        label={location === 'checkout' ? 'Back to checkout' : 'Done'}
        onClick={() => onDoneClick(optOut)}
        variant="primary"
      />
    </Flex>
  );
};
